import { LoadingButton } from "@mui/lab";
import { Box, Stepper, Step, StepLabel, Button, Divider } from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { PentaSplit } from "../../../../components/common/PentaSplit";
import { graphQLServices } from "../../../../services/GraphQLServices";
import {CREATE_MAIL_SETS} from "../../graphql/mutations/gqlCreateMailSet";
import { MailexUploadRulesDto } from "../../models/MailexUploadRulesDto";
import {MailexConfirmationStep} from "./steps/MailexConfirmationStep";
import {MailexCreateRulesStep} from "./steps/MailexCreateRulesStep";
import {MailexUploadStep} from "./steps/MailexUploadStep";

interface IMailexUploadProps {
	success: () => void;
}

export const MailexUpload: React.FC<IMailexUploadProps> = (props) => {
	const [files, setFiles] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);
	const [rules, setRules] = useState<MailexUploadRulesDto>({
		datetime: moment().add(10, "minutes").toDate(),
	});
	const [currentStepValid, setCurrentStepValid] = useState(true);

	const content: { step: string; content?: JSX.Element }[] = [
		{
			step: "Upload a file",
			content: (
				<MailexUploadStep
					files={files}
					setFiles={setFiles}
					validate={setCurrentStepValid}
				/>
			),
		},
		{
			step: "Create rules",
			content: (
				<MailexCreateRulesStep
					rules={rules}
					setRules={setRules}
					validate={setCurrentStepValid}
				/>
			),
		},
		{
			step: "Finalise",
			content: <MailexConfirmationStep files={files} rules={rules} />,
		},
	];

	const [stepIndex, setStepIndex] = useState<number>(0);

	const next = () => {
		if (stepIndex === content.length - 1) {
			setLoading(true);
			files.forEach((file) => {
				graphQLServices.mailexClient
					.mutate({
						mutation: CREATE_MAIL_SETS,
						variables: {
							description: rules.description,
							template: rules.template,
							datetime: rules.datetime,
						},
					})
					.then(async (result) => {
						const presignedUrl = result.data.createMailSet.presignedUrl;
						const myHeaders = new Headers({
							"Content-Type": "application/zip",
						});
						const response = await fetch(presignedUrl, {
							method: "PUT",
							body: file,
							headers: myHeaders,
						});

						if (response.ok === true) {
							toast.success("Upload succeeded!");
						} else {
							toast.error("Upload failed!");
						}
					});
			});
			setLoading(false);
			props.success();
		} else {
			setStepIndex(stepIndex + 1);
		}
	};

	const previous = () => {
		setStepIndex(stepIndex - 1);
	};

	return (
		<>
			<Box sx={{ width: "100%" }}>
				<Stepper activeStep={stepIndex} alternativeLabel>
					{content.map((step) => (
						<Step key={step.step}>
							<StepLabel>{step.step}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Box>
			<Box sx={{ height: "100%" }}>
				<PentaSplit
					center={
						<>
							<Divider sx={{ mt: "18px", mb: "18px" }} />
							<Box>{content[stepIndex].content}</Box>
						</>
					}
					bottom={
						<>
							<Divider sx={{ mt: "18px", mb: "18px" }} />
							<Box sx={{ display: "flex", justifyContent: "space-between" }}>
								<Button
									variant="outlined"
									onClick={() => previous()}
									disabled={stepIndex === 0}
								>
									Previous
								</Button>
								<LoadingButton
									disabled={!currentStepValid}
									variant="outlined"
									onClick={() => next()}
									loading={loading}
								>
									{stepIndex === content.length - 1 ? "Complete" : "Next"}
								</LoadingButton>
							</Box>
						</>
					}
				/>
			</Box>
		</>
	);
};
